import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import { PageHeader } from '@/components/composites/PageHeader';
import { EmployeeSwitcher } from '@/components/composites/EmployeeSwitcher';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Select } from '@/components/primitives/Select';
import { Badge } from '@/components/primitives/Badge';
import { useAppContext } from '@/context/AppContext';
import { useEmployees } from '@/hooks/useEmployees';
import { useTimesheets } from '@/hooks/useTimesheets';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useWorkblocksRange } from '@/hooks/useWorkblocksRange';
import { useWorkblocksDay } from '@/hooks/useWorkblocksDay';
import { formatDayShort, formatDayWithName } from '@/lib/dates';
import {
  projectColorAt,
  makeProjectColorForId,
  buildDayBlocksFromWorkblocks,
  clampBlocks,
  type DayBlock,
} from '@/lib/timelineBlocks';
import type { TimesheetDailyRow } from '@/data/timesheets';
import type { WorkBlocksRangeDay } from '@/types/dashboard';
import styles from './TimesheetRangePage.module.css';

/* ── Helpers ── */

const DAY_TYPE_LABEL: Record<TimesheetDailyRow['dayType'], string> = {
  full: 'Full Day',
  half: 'Half Day',
  checkin: 'Check-in',
};

function dayOfWeek(iso: string): string {
  const full = formatDayWithName(iso);
  const parts = full.split(', ');
  return parts[1] ?? full;
}

function formatHour(hour: number): string {
  if (hour === 0 || hour === 24) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

/** Format minutes-since-midnight as "9:30 AM". */
function formatMinuteTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

/** Format a duration in minutes as "1h 30m". */
function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Mockup path — synthesize block positions from dayType + a string-hash
 * seed over the date. Kept verbatim from the pre-task-128 implementation
 * (same output, same snapshot). Only reachable when mockupMode is true.
 */
function buildDayBlocksFromDayType(
  row: TimesheetDailyRow,
  viewStartHour: number,
  viewEndHour: number,
): DayBlock[] {
  const viewRange = (viewEndHour - viewStartHour) * 60;
  const seed = row.date.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const workStartMin = viewStartHour * 60 + 30 + (seed % 60);

  const blocks: DayBlock[] = [];
  let cursor = workStartMin;

  function push(dur: number, colorToken: string, label: string, isWork: boolean) {
    const start = cursor;
    const end = cursor + dur;
    blocks.push({
      leftPct: ((start - viewStartHour * 60) / viewRange) * 100,
      widthPct: (dur / viewRange) * 100,
      colorToken,
      label,
      isWork,
      isUnclassified: false,
      startMin: start,
      endMin: end,
      eventCount: 0,
    });
    cursor = end;
  }

  if (row.dayType === 'checkin') {
    const dur = Math.round(row.totalHours * 60);
    push(dur, projectColorAt(seed), 'Work', true);
  } else if (row.dayType === 'half') {
    const workMin = Math.round(row.totalHours * 60);
    const seg1 = Math.round(workMin * 0.6);
    const seg2 = workMin - seg1;
    const breakMin = 15 + (seed % 15);

    push(seg1, projectColorAt(seed), 'Work', true);
    push(breakMin, '--color-break', 'Break', false);
    push(seg2, projectColorAt(seed + 1), 'Work', true);
  } else {
    // Full day — 3 work segments + 2 breaks
    const workMin = Math.round(row.totalHours * 60);
    const seg1 = Math.round(workMin * 0.35);
    const seg2 = Math.round(workMin * 0.35);
    const seg3 = workMin - seg1 - seg2;
    const break1 = 45 + (seed % 30); // lunch
    const break2 = 10 + (seed % 10);

    push(seg1, projectColorAt(seed), 'Work', true);
    push(break1, '--color-break', 'Break', false);
    push(seg2, projectColorAt(seed + 1), 'Work', true);
    push(break2, '--color-break', 'Break', false);
    push(seg3, projectColorAt(seed + 2), 'Work', true);
  }

  return clampBlocks(blocks);
}

/** Check if any work block extends beyond the visible view-window hours. */
function hasOverflow(
  blocks: readonly DayBlock[],
  viewStartHour: number,
  viewEndHour: number,
): boolean {
  const viewStartMin = viewStartHour * 60;
  const viewEndMin = viewEndHour * 60;
  return blocks.some((b) => b.isWork && (b.startMin < viewStartMin || b.endMin > viewEndMin));
}

/* ── Hour options for view-window selectors ── */
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: formatHour(i),
}));

/* ── Chevron icon (inline SVG) ── */
function ChevronRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

/* ── DayTimelineCard (page-local component) ── */

interface DayTimelineCardProps {
  day: WorkBlocksRangeDay;
  memberId: string;
  viewStartHour: number;
  viewEndHour: number;
  /** Mockup-mode fallback row (non-null only in mockup mode). */
  mockupRow: TimesheetDailyRow | null;
  tz: string;
  projectColorForId: (id: string | null) => string;
}

function DayTimelineCard({
  day,
  memberId,
  viewStartHour,
  viewEndHour,
  mockupRow,
  tz,
  projectColorForId,
}: DayTimelineCardProps) {
  const navigate = useNavigate();
  // In mockup mode the hook returns `null` immediately — no network call.
  const dayQuery = useWorkblocksDay(memberId, day.date);
  const totalHours = day.totalWorkMinutes / 60;

  const handleClick = () => {
    navigate(`/timesheet/daily?member=${memberId}&date=${day.date}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Pick the block source for the current mode.
  let blocks: DayBlock[] = [];
  let loading = false;
  let errored = false;
  if (mockupRow) {
    blocks = buildDayBlocksFromDayType(mockupRow, viewStartHour, viewEndHour);
  } else if (dayQuery.isLoading) {
    loading = true;
  } else if (dayQuery.error) {
    errored = true;
  } else if (dayQuery.data) {
    blocks = buildDayBlocksFromWorkblocks(
      dayQuery.data,
      viewStartHour,
      viewEndHour,
      tz,
      projectColorForId,
    );
  }

  const overflow = hasOverflow(blocks, viewStartHour, viewEndHour);

  return (
    <div
      className={styles.dayCard}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label={`${dayOfWeek(day.date)} ${formatDayShort(day.date)}, ${totalHours.toFixed(1)} hours`}
    >
      {/* Date column */}
      <div className={styles.dateCol}>
        <div className={styles.dateDow}>{dayOfWeek(day.date)}</div>
        <div className={styles.dateLabel}>{formatDayShort(day.date)}</div>
      </div>

      {/* Timeline bar */}
      <div className={styles.timelineCol}>
        {loading ? (
          <div className={styles.timelineTrackSkeleton} aria-label="Loading day timeline" />
        ) : (
          <div className={styles.timelineTrack}>
            {blocks
              .filter((b) => b.widthPct > 0)
              .map((block, i) => (
                <div
                  key={i}
                  className={
                    block.isUnclassified
                      ? `${styles.timelineBlock} ${styles.timelineBlockUnclassified}`
                      : styles.timelineBlock
                  }
                  style={{
                    left: `${block.leftPct}%`,
                    width: `${block.widthPct}%`,
                    backgroundColor: `var(${block.colorToken})`,
                  }}
                  title={`${block.label}: ${formatMinuteTime(block.startMin)} – ${formatMinuteTime(block.endMin)} (${formatDuration(block.endMin - block.startMin)})`}
                />
              ))}
          </div>
        )}
      </div>

      {/* Warning / error indicator */}
      <div className={styles.warningCol}>
        {errored ? (
          <span className={styles.cardErrorNote} title="Could not load">
            !
          </span>
        ) : (
          overflow && <WarningIcon />
        )}
      </div>

      {/* Hours + day type (day-type badge only in mockup mode — live mode
          does not classify days into types; see Non-goals). */}
      <div className={styles.hoursCol}>
        <div className={styles.hoursValue}>{totalHours.toFixed(1)}h</div>
        {mockupRow && (
          <div className={styles.hoursSub}>
            <Badge variant={mockupRow.dayType}>{DAY_TYPE_LABEL[mockupRow.dayType]}</Badge>
          </div>
        )}
      </div>

      {/* Chevron */}
      <div className={styles.chevronCol}>
        <ChevronRight />
      </div>
    </div>
  );
}

/* ── Empty day card ── */

function EmptyDayCard({ date }: { date: string }) {
  return (
    <div className={styles.dayCardEmpty}>
      <div className={styles.dateCol}>
        <div className={styles.dateDow}>{dayOfWeek(date)}</div>
        <div className={styles.dateLabel}>{formatDayShort(date)}</div>
      </div>
      <div className={styles.timelineCol}>
        <div className={styles.emptyTrack}>No activity</div>
      </div>
      <div className={styles.warningCol} />
      <div className={styles.hoursCol}>
        <div className={styles.hoursEmpty}>0h</div>
      </div>
      <div className={styles.chevronCol} />
    </div>
  );
}

/* ── Time axis ── */

function TimeAxis({ startHour, endHour }: { startHour: number; endHour: number }) {
  const midHour = Math.floor((startHour + endHour) / 2);
  const q1Hour = Math.floor((startHour + midHour) / 2);
  const q3Hour = Math.floor((midHour + endHour) / 2);
  const ticks = [startHour, q1Hour, midHour, q3Hour, endHour];

  return (
    <div className={styles.axisRow}>
      <div className={styles.axisDateSpacer} />
      <div className={styles.axisTrack}>
        {ticks.map((h) => {
          const pct = ((h - startHour) / (endHour - startHour)) * 100;
          return (
            <span key={h} className={styles.axisTick} style={{ left: `${pct}%` }}>
              {formatHour(h)}
            </span>
          );
        })}
      </div>
      <div className={styles.axisChevronSpacer} />
      <div className={styles.axisHoursSpacer} />
      <div className={styles.axisChevronSpacer} />
    </div>
  );
}

/* ── Main page ── */

export function TimesheetRangePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dateRange, mockupMode } = useAppContext();
  const { data: employees } = useEmployees();
  const { data: timesheets } = useTimesheets();
  const { data: orgSettings } = useOrgSettings();

  // D7 — live mode has no mockup fixture employees; fall back to the first
  // visible one. Mockup mode keeps the pre-task-128 sarah-chen default so
  // the snapshot stays green.
  const memberParam = searchParams.get('member');
  const memberFallback = mockupMode ? 'emp-sarah-chen' : (employees[0]?.id ?? null);
  const memberId = memberParam ?? memberFallback ?? null;

  // Surface the chosen employee in the URL so share-links work and the
  // switcher reflects the resolved selection.
  useEffect(() => {
    if (!memberParam && memberFallback) {
      const next = new URLSearchParams(searchParams);
      next.set('member', memberFallback);
      setSearchParams(next, { replace: true });
    }
  }, [memberParam, memberFallback, searchParams, setSearchParams]);

  const viewStartHour = Number(searchParams.get('viewStart') ?? '7');
  const viewEndHour = Number(searchParams.get('viewEnd') ?? '19');

  const employee = employees.find((e) => e.id === memberId) ?? employees[0];

  const range = useWorkblocksRange(memberId, dateRange.start, dateRange.end);

  // Mockup-mode fallback — the seed-based block layout still reads the
  // full TimesheetDailyRow, not just the range day. Build a `date → row`
  // lookup from the fixture so DayTimelineCard can grab the right row.
  const mockupRowsByDate: Record<string, TimesheetDailyRow> = {};
  if (mockupMode && employee) {
    const bundle = timesheets[employee.id];
    for (const r of bundle?.dailyRows ?? []) mockupRowsByDate[r.date] = r;
  }

  // Task 152/153: orgSettings.timezone is a non-null string post-load. The
  // optional-chain default covers only the loading window; 'UTC' matches
  // the server-side default, so a flash during load reads honestly instead
  // of pretending the org is on America/New_York.
  const tz = orgSettings?.timezone ?? 'UTC';

  // Deterministic projectId → colour map — computed once per render from
  // the visible range's project id order. Unclassified blocks (projectId
  // null) do NOT take a slot; they render on the fallback token with 0.5
  // opacity via the `.timelineBlockUnclassified` CSS rule.
  const projectColorForId = makeProjectColorForId();

  const rangeData = range.data;
  // Most recent first.
  const days = rangeData ? [...rangeData.days].sort((a, b) => b.date.localeCompare(a.date)) : [];

  const totalHours = (rangeData?.summary.totalWorkMinutes ?? 0) / 60;
  const daysActive = rangeData?.summary.daysWithActivity ?? 0;
  const avgPerDay = (rangeData?.summary.avgWorkMinutesPerDay ?? 0) / 60;
  // Billable % — the /workblocks-range endpoint does not return billable
  // totals today. Extending it is an explicit separate task (see Task 128
  // § Non-goals: "Billable vs. general colour split within a work block"
  // and "Out of scope: Billable % on the range endpoint"). Leave at 0 in
  // live mode; mockup mode derives from the fixture.
  const billablePct = (() => {
    if (mockupMode && employee) {
      const bundle = timesheets[employee.id];
      const rows = bundle?.dailyRows ?? [];
      const mockTotal = rows.reduce((s, r) => s + r.totalHours, 0);
      const mockBillable = rows.reduce((s, r) => s + r.billableHours, 0);
      return mockTotal > 0 ? Math.round((mockBillable / mockTotal) * 100) : 0;
    }
    return 0;
  })();

  const loadingPlaceholder = range.isLoading;
  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    {
      label: 'Total Hours',
      value: loadingPlaceholder ? '…' : `${totalHours.toFixed(1)}h`,
    },
    { label: 'Days Active', value: loadingPlaceholder ? '…' : daysActive },
    {
      label: 'Avg Hours/Day',
      value: loadingPlaceholder ? '…' : daysActive > 0 ? `${avgPerDay.toFixed(1)}h` : '0h',
    },
    { label: 'Billable %', value: loadingPlaceholder ? '…' : `${billablePct}%` },
  ];

  const handleMemberSwitch = (id: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('member', id);
    setSearchParams(params);
  };

  const handleViewStartChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('viewStart', value);
    setSearchParams(params);
  };

  const handleViewEndChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('viewEnd', value);
    setSearchParams(params);
  };

  return (
    <>
      <PageHeader
        title="Timesheet"
        subtitle={employee?.name}
        actions={
          memberId ? (
            <EmployeeSwitcher currentEmployeeId={memberId} onSwitch={handleMemberSwitch} />
          ) : null
        }
      />

      {/* Controls row */}
      <div className={styles.controls}>
        <div className={styles.viewControls}>
          <Select
            label="From"
            size="sm"
            value={String(viewStartHour)}
            options={HOUR_OPTIONS.slice(0, 23)}
            onChange={handleViewStartChange}
          />
          <span className={styles.viewSep}>&ndash;</span>
          <Select
            label="To"
            size="sm"
            value={String(viewEndHour)}
            options={HOUR_OPTIONS.slice(1)}
            onChange={handleViewEndChange}
          />
        </div>
      </div>

      <StatCardGrid cards={cards} />

      {/* Time axis */}
      <TimeAxis startHour={viewStartHour} endHour={viewEndHour} />

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: 'var(--color-project-a)' }} />
          Billable
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: 'var(--color-project-b)' }} />
          General
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: 'var(--color-break)' }} />
          Break
        </div>
        <div className={styles.legendItem}>
          <span className={styles.warningCol}>
            <WarningIcon />
          </span>
          Outside range
        </div>
        <span className={styles.legendHint}>Click a day for details</span>
      </div>

      {/* Day cards */}
      <div className={styles.dayStack}>
        {!memberId && !loadingPlaceholder && (
          <EmptyState title="Pick an employee" description="No team member is selected." />
        )}
        {range.error && (
          <EmptyState
            title="Could not load timesheet"
            description={range.error.message || 'The range request failed.'}
          />
        )}
        {!range.error && memberId && loadingPlaceholder && (
          <EmptyState title="Loading timesheet…" />
        )}
        {!range.error && memberId && !loadingPlaceholder && days.length === 0 && (
          <EmptyState
            title="No activity in this range"
            description={employee ? `No recorded activity for ${employee.name}.` : undefined}
          />
        )}
        {!range.error &&
          memberId &&
          !loadingPlaceholder &&
          days.map((d) =>
            d.eventCount === 0 ? (
              <EmptyDayCard key={d.date} date={d.date} />
            ) : (
              <DayTimelineCard
                key={d.date}
                day={d}
                memberId={memberId}
                viewStartHour={viewStartHour}
                viewEndHour={viewEndHour}
                mockupRow={mockupMode ? (mockupRowsByDate[d.date] ?? null) : null}
                tz={tz}
                projectColorForId={projectColorForId}
              />
            ),
          )}
      </div>

      {/* Footer summary */}
      <div className={styles.footer}>
        <span className={styles.footerLabel}>Total</span>
        <span className={styles.footerStat}>{totalHours.toFixed(1)}h</span>
        <span className={styles.footerSep}>|</span>
        <span className={styles.footerStat}>{daysActive} days</span>
        <span className={styles.footerSep}>|</span>
        <span className={styles.footerStat}>{billablePct}% billable</span>
      </div>
    </>
  );
}
